import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Minus, Plus, ShieldCheck, Ticket, X } from 'lucide-react';
import { CartItem } from '../contexts/CartContext';
import { getFullImageUrl } from '../services/api';
import { PEEKPASS_BRAND } from '../utils/peekPassBrand';

export interface PaymentMethod {
  id: 'mercadopago';
  name: string;
  description: string;
}

export interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPaymentMethodSelect: (method: PaymentMethod) => Promise<void> | void;
  total: number;
  currency: string;
  itemCount: number;
  items?: CartItem[];
  onUpdateQuantity?: (itemId: string, quantity: number) => void;
}

const mercadoPago: PaymentMethod = {
  id: 'mercadopago',
  name: 'Mercado Pago',
  description: 'Pago seguro para confirmar tus entradas.'
};

const formatMoney = (value: number, currency: string) => new Intl.NumberFormat('es-AR', {
  style: 'currency',
  currency,
  maximumFractionDigits: 2
}).format(value);

const roundMoney = (value: number) => Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;

const normalizeServiceFeeRate = (value?: number) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue >= 5 && numericValue <= 18 ? numericValue : 5;
};

const PaymentModal: React.FC<PaymentModalProps> = ({
  isOpen,
  onClose,
  onPaymentMethodSelect,
  total,
  currency,
  itemCount,
  items = [],
  onUpdateQuantity
}) => {
  const [isProcessing, setIsProcessing] = useState(false);

  const subtotal = useMemo(() => (
    items.length
      ? items.reduce((sum, item) => sum + item.price * (item.quantity || 1), 0)
      : total
  ), [items, total]);
  const checkout = useMemo(() => getPeekPassCheckoutTotal(subtotal, items), [items, subtotal]);

  useEffect(() => {
    if (!isOpen) return;

    setIsProcessing(false);
    const handlePopState = () => onClose();
    window.history.pushState({ page: 'peekpass-checkout' }, '', window.location.href);
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isOpen, onClose]);

  const handlePayment = async () => {
    setIsProcessing(true);
    try {
      await onPaymentMethodSelect(mercadoPago);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] flex items-end bg-black/55 p-0 backdrop-blur-sm sm:items-center sm:justify-center sm:p-5"
          onClick={onClose}
        >
          <motion.section
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.2 }}
            onClick={(event) => event.stopPropagation()}
            aria-modal="true"
            aria-label="Finalizar compra"
            role="dialog"
            className="max-h-[94vh] w-full max-w-5xl overflow-y-auto rounded-t-[2rem] bg-[#f8f5ef] text-slate-950 shadow-[0_35px_110px_-42px_rgba(0,0,0,0.72)] sm:rounded-[2rem]"
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4 sm:px-7">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.24em] text-[#71931b]">{PEEKPASS_BRAND}</p>
                <h2 className="mt-1 text-2xl font-black tracking-[-0.05em]">Revisá tu compra</h2>
              </div>
              <button type="button" onClick={onClose} className="inline-flex h-10 w-10 items-center justify-center border border-slate-300 bg-white text-slate-950 transition hover:border-black" aria-label="Cerrar"><X className="h-5 w-5" /></button>
            </div>

            <div className="grid lg:grid-cols-[minmax(0,1fr)_340px]">
              <div className="p-5 sm:p-7">
                <div className="flex items-center justify-between gap-4">
                  <h3 className="text-lg font-black">Entradas seleccionadas</h3>
                  <span className="text-sm font-semibold text-slate-500">{itemCount} {itemCount === 1 ? 'entrada' : 'entradas'}</span>
                </div>
                <div className="mt-5 divide-y divide-slate-200 border-y border-slate-200">
                  {items.length ? items.map((item) => {
                    const quantity = item.quantity || 1;
                    return (
                      <article key={item.id} className="flex gap-4 py-5">
                        <div className="hidden h-16 w-16 shrink-0 overflow-hidden bg-slate-200 sm:block">
                          {item.image ? <img src={/^(https?:|data:|blob:)/i.test(item.image) ? item.image : getFullImageUrl(item.image)} alt="" className="h-full w-full object-cover" /> : <Ticket className="m-5 h-6 w-6 text-slate-500" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[10px] font-black uppercase tracking-[0.16em] text-[#71931b]">{item.eventDate || item.category}</p>
                          <h4 className="mt-1 truncate text-base font-black">{item.title}</h4>
                          <p className="mt-1 truncate text-sm text-slate-600">{item.venue || item.description}</p>
                          <p className="mt-2 text-sm font-semibold text-slate-700">{formatMoney(item.price, item.currency)} por entrada</p>
                        </div>
                        <div className="flex shrink-0 flex-col items-end justify-between gap-3">
                          {onUpdateQuantity ? (
                            <div className="inline-flex items-center border border-slate-300 bg-white p-1">
                              <button type="button" onClick={() => onUpdateQuantity(item.id, quantity - 1)} disabled={quantity <= 1} className="grid h-8 w-8 place-items-center text-slate-700 transition hover:bg-[#eef8ca] disabled:cursor-not-allowed disabled:opacity-30" aria-label="Restar una entrada"><Minus className="h-4 w-4" /></button>
                              <span className="w-8 text-center text-sm font-black">{quantity}</span>
                              <button type="button" onClick={() => onUpdateQuantity(item.id, quantity + 1)} className="grid h-8 w-8 place-items-center text-slate-700 transition hover:bg-[#eef8ca]" aria-label="Sumar una entrada"><Plus className="h-4 w-4" /></button>
                            </div>
                          ) : <span className="text-sm font-bold">x{quantity}</span>}
                          <p className="text-sm font-black">{formatMoney(item.price * quantity, item.currency)}</p>
                        </div>
                      </article>
                    );
                  }) : (
                    <article className="py-5 text-sm text-slate-600">La cantidad elegida se confirmará al continuar.</article>
                  )}
                </div>
              </div>

              <aside className="border-t border-slate-200 bg-white p-5 lg:border-l lg:border-t-0 sm:p-7">
                <p className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-500">Resumen</p>
                <div className="mt-5 space-y-3 text-sm">
                  <div className="flex items-center justify-between gap-4"><span className="text-slate-600">Valor de la entrada</span><span className="font-bold">{formatMoney(checkout.subtotal, currency)}</span></div>
                  <div className="flex items-center justify-between gap-4"><span className="text-slate-600">Cargo por servicio</span><span className="font-bold">{formatMoney(checkout.fee, currency)}</span></div>
                  <div className="flex items-end justify-between gap-4 border-t border-slate-200 pt-4"><span className="text-base font-black">Total a pagar</span><span className="text-2xl font-black tracking-[-0.05em]">{formatMoney(checkout.total, currency)}</span></div>
                </div>

                <div className="mt-7 border border-[#c8df70] bg-[#f4fbd9] p-4">
                  <div className="flex items-center gap-3"><div className="grid h-9 w-9 place-items-center bg-[#dff38c] text-xs font-black text-black">MP</div><div><p className="text-sm font-black">Mercado Pago</p><p className="text-xs text-slate-600">{mercadoPago.description}</p></div></div>
                </div>
                <div className="mt-5 flex items-start gap-2 text-xs leading-5 text-slate-500"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#71931b]" />El pago se completa de forma segura en Mercado Pago.</div>
                <button type="button" onClick={handlePayment} disabled={isProcessing} className="mt-6 w-full bg-black px-5 py-4 text-sm font-black text-white transition hover:bg-[#536d12] disabled:cursor-wait disabled:bg-slate-500">{isProcessing ? 'Abriendo Mercado Pago...' : 'Pagar con Mercado Pago'}</button>
              </aside>
            </div>
          </motion.section>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};

export const getPeekPassCheckoutTotal = (subtotal: number, items?: CartItem[]) => {
  const lines = (items || []).map((item) => {
    const quantity = item.quantity || 1;
    const entryValue = roundMoney(item.price * quantity);
    const serviceFeeRate = normalizeServiceFeeRate(item.serviceFeeRate);
    const serviceFee = roundMoney(entryValue * serviceFeeRate / 100);

    return {
      itemId: item.id,
      title: item.title,
      ticketTierName: item.ticketTierName,
      quantity,
      entryValue,
      serviceFeeRate,
      serviceFee,
      total: roundMoney(entryValue + serviceFee)
    };
  });

  const ticketSubtotal = lines.length ? roundMoney(lines.reduce((sum, line) => sum + line.entryValue, 0)) : roundMoney(subtotal);
  const serviceFeeAmount = lines.length ? roundMoney(lines.reduce((sum, line) => sum + line.serviceFee, 0)) : roundMoney(ticketSubtotal * 0.05);

  return {
    subtotal: ticketSubtotal,
    fee: serviceFeeAmount,
    serviceFeeAmount,
    total: roundMoney(ticketSubtotal + serviceFeeAmount),
    lines
  };
};

export default PaymentModal;
